/**
 * Hook for managing publish status in the admin panel
 *
 * Polls the backend for pending changes status and provides
 * functions to trigger/cancel publish.
 */
import { useState, useEffect, useCallback } from 'react';

import { get, post } from '../services/api/client';

const POLL_INTERVAL = 30000; // Poll every 30 seconds

export const usePublishStatus = () => {
  const [status, setStatus] = useState({
    hasPendingChanges: false,
    lastChangeAt: null,
    scheduledPublishAt: null,
    lastPublishedAt: null,
    pendingChanges: [],
    autoPublishDelayMinutes: 5,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await get('/publish/status');
      // Helper to parse UTC datetime strings - ensures they're treated as UTC
      const parseUTCDate = (dateStr) => {
        if (!dateStr) return null;
        // If the string doesn't end with Z or timezone offset, append Z to treat as UTC
        if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
          dateStr = dateStr + 'Z';
        }
        return new Date(dateStr);
      };

      setStatus({
        hasPendingChanges: data.has_pending_changes,
        lastChangeAt: parseUTCDate(data.last_change_at),
        scheduledPublishAt: parseUTCDate(data.scheduled_publish_at),
        lastPublishedAt: parseUTCDate(data.last_published_at),
        pendingChanges: data.pending_changes || [],
        autoPublishDelayMinutes: data.auto_publish_delay_minutes || 5,
      });
      setError(null);
    } catch (err) {
      // Silently fail - endpoint may not exist yet
      console.warn('Failed to fetch publish status:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerPublish = useCallback(async () => {
    setPublishing(true);
    try {
      const result = await post('/publish/trigger', {});
      await fetchStatus();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setPublishing(false);
    }
  }, [fetchStatus]);

  const cancelPublish = useCallback(async () => {
    try {
      await post('/publish/cancel', {});
      await fetchStatus();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchStatus]);

  const reschedulePublish = useCallback(async () => {
    try {
      await post('/publish/reschedule', {});
      await fetchStatus();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchStatus]);

  // Initial fetch and polling
  useEffect(() => {
    fetchStatus();

    const interval = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return {
    ...status,
    loading,
    error,
    publishing,
    refresh: fetchStatus,
    triggerPublish,
    cancelPublish,
    reschedulePublish,
  };
};

export default usePublishStatus;
